import { getPremiumPricingMode } from './insightPricingDetails';

/** @typedef {'insight' | 'premium' | 'pro'} PricingCategory */

/**
 * Resolve pricing category from subscription plan `planType` prefix.
 * Suffix may vary; only the prefix determines the user category.
 *
 * @param {string|null|undefined} planType
 * @returns {PricingCategory}
 */
export function resolvePricingCategoryFromPlanType(planType) {
  const pt = String(planType || '').trim();
  if (pt.startsWith('Prem_')) return 'premium';
  if (pt.startsWith('New_')) return 'pro';
  if (pt.startsWith('Insight_')) return 'insight';
  // Legacy API values (e.g. "InsightSubscription")
  if (/^insight/i.test(pt)) return 'insight';
  return 'insight';
}

/**
 * Derive pricing category from subscription plans bucket (monthly / 1year / 3years).
 *
 * @param {Record<string, { planType?: string }|null>|null|undefined} plans
 * @returns {PricingCategory}
 */
export function resolvePricingCategoryFromPlans(plans) {
  if (!plans || typeof plans !== 'object') return 'insight';

  const rows = [plans.monthly, plans['1year'], plans['3years']].filter(Boolean);
  if (!rows.length) return 'insight';

  const category = resolvePricingCategoryFromPlanType(rows[0]?.planType);

  if (process.env.NODE_ENV !== 'production') {
    const mismatched = rows.filter(
      (row) => resolvePricingCategoryFromPlanType(row?.planType) !== category
    );
    if (mismatched.length) {
      console.warn(
        '[subscriptionPricing] planType prefix mismatch across frequency rows; using category from first row:',
        category,
        rows.map((r) => r?.planType)
      );
    }
  }

  return category;
}

/**
 * True when premium uses flat-rate pricing (old card layout + flat-rate popup).
 * `premiumPricingMode` applies to monthly, 1-year, and 3-year together — not per plan.
 * @param {PricingCategory} pricingCategory
 * @returns {boolean}
 */
export function isPremiumFlatRateCategory(pricingCategory) {
  return pricingCategory === 'premium' && getPremiumPricingMode() === 'old';
}
